import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  FaBoxes,
  FaFolder,
  FaTags,
  FaStore,
  FaBuilding,
  FaNewspaper,
  FaCalendarAlt,
  FaFileAlt,
  FaThLarge,
  FaCommentAlt,
  FaSync,
  FaArrowRight,
  FaPlusCircle,
  FaCheckCircle,
} from 'react-icons/fa';
import './Dashboard.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api/v1/';

function Home() {
  const [stats, setStats] = useState({
    products: 0,
    categories: 0,
    attributes: 0,
    dealers: 0,
    showroomCategories: 0,
    blogs: 0,
    events: 0,
    pages: 0,
    widgets: 0,
    testimonials: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const url = API_BASE.endsWith('/') ? `${API_BASE}dashboard` : `${API_BASE}/dashboard`;
      const res = await axios.get(url);
      if (res.data && res.data.data) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const menuCards = [
    {
      title: 'Products Manager',
      count: stats.products,
      label: 'Active Products',
      icon: <FaBoxes size={22} />,
      link: '/products',
      desc: 'Wardrobes, kitchens & partition models live in catalog',
      addLink: '/products',
    },
    {
      title: 'Categories Manager',
      count: stats.categories,
      label: 'Active Categories',
      icon: <FaFolder size={22} />,
      link: '/categories',
      desc: 'Sliding, openable, kitchen & interior categories',
      addLink: '/categories',
    },
    {
      title: 'Attribute Manager',
      count: stats.attributes,
      label: 'Active Attributes',
      icon: <FaTags size={22} />,
      link: '/attributes',
      desc: 'Finishes, dimensions, profiles & active specifications',
      addLink: '/attributes',
    },
    {
      title: 'Dealer Store / Showrooms',
      count: stats.dealers,
      label: 'Active Showrooms',
      icon: <FaStore size={22} />,
      link: '/dealer-store',
      desc: 'Active dealer stores and experience centers across India',
      addLink: '/dealer-store',
    },
    {
      title: 'Showroom Categories',
      count: stats.showroomCategories,
      label: 'Active Classifications',
      icon: <FaBuilding size={22} />,
      link: '/showroom-categories',
      desc: 'Experience centers, studio & partner classifications',
      addLink: '/showroom-categories',
    },
    {
      title: 'Blog Manager',
      count: stats.blogs,
      label: 'Active Blog Posts',
      icon: <FaNewspaper size={22} />,
      link: '/blog-list',
      desc: 'Design inspirations, trend stories and architectural news',
      addLink: '/add-blog',
    },
    {
      title: 'Event Manager',
      count: stats.events,
      label: 'Active Events',
      icon: <FaCalendarAlt size={22} />,
      link: '/event-list',
      desc: 'Showcase events, design summits and showroom launches',
      addLink: '/add-event',
    },
    {
      title: 'Page Management',
      count: stats.pages,
      label: 'Active Pages',
      icon: <FaFileAlt size={22} />,
      link: '/page-list',
      desc: 'Home, About, Advantage, Store Locator & policy pages',
      addLink: '/page-list',
    },
    {
      title: 'Widget Manager',
      count: stats.widgets,
      label: 'Active Widgets',
      icon: <FaThLarge size={22} />,
      link: '/widget-manager',
      desc: 'Homepage carousels, dynamic banners & UI sections',
      addLink: '/widget-manager',
    },
    {
      title: 'Testimonials Manager',
      count: stats.testimonials,
      label: 'Active Reviews',
      icon: <FaCommentAlt size={22} />,
      link: '/testimonial-list',
      desc: 'Verified homeowner and architect testimonials live',
      addLink: '/add-testimonial',
    },
  ];

  return (
    <div className="premium-dashboard-container">
      {/* Dashboard Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-3" style={{ borderBottom: '1px solid rgba(196, 168, 130, 0.2)' }}>
        <div>
          <h1 className="dashboard-title mb-1" style={{ color: '#c4a882', fontWeight: 800, letterSpacing: '1px' }}>
            Dashboard Overview
          </h1>
          <p style={{ color: '#8a8377', fontSize: '13px', margin: 0 }}>
            Welcome to ARISTO Management Portal. Showing real-time active system records.
          </p>
        </div>
        <div className="d-flex align-items-center mt-2 mt-md-0" style={{ gap: '12px' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: 'rgba(72, 187, 120, 0.1)',
              border: '1px solid rgba(72, 187, 120, 0.3)',
              color: '#48bb78',
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            <FaCheckCircle size={12} color="#48bb78" />
            Active Records Only
          </span>
          <button
            onClick={fetchDashboardStats}
            className="btn btn-sm"
            style={{
              backgroundColor: 'rgba(196, 168, 130, 0.12)',
              border: '1px solid rgba(196, 168, 130, 0.35)',
              color: '#c4a882',
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
            title="Refresh Live Metrics"
          >
            <FaSync className={loading ? 'fa-spin' : ''} size={11} /> Refresh
          </button>
        </div>
      </div>

      {/* Main Sidebar Menus Stats Cards Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px',
          marginBottom: '36px',
        }}
      >
        {menuCards.map((card, index) => (
          <div
            key={index}
            className="premium-stat-card"
            style={{
              backgroundColor: '#141310',
              border: '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '14px',
              padding: '22px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              transition: 'all 0.3s ease',
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            }}
          >
            <div>
              {/* Card Top: Title + Icon */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1.2px',
                    color: '#8a8377',
                  }}
                >
                  {card.title}
                </span>
                <div
                  style={{
                    width: '46px',
                    height: '46px',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(196, 168, 130, 0.12)',
                    border: '1px solid rgba(196, 168, 130, 0.3)',
                    color: '#c4a882',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {card.icon}
                </div>
              </div>

              {/* Count Number */}
              <div style={{ fontSize: '36px', fontWeight: 800, color: '#ffffff', lineHeight: '1.1', marginBottom: '4px' }}>
                {loading ? '...' : card.count}
              </div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#c4a882', marginBottom: '8px' }}>
                {card.label}
              </div>
              <p style={{ fontSize: '12px', color: '#666', lineHeight: '1.4', margin: 0, minHeight: '34px' }}>
                {card.desc}
              </p>
            </div>

            {/* Bottom Action Links */}
            <div
              className="d-flex justify-content-between align-items-center pt-3 mt-3"
              style={{ borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}
            >
              <Link
                to={card.link}
                style={{
                  color: '#c4a882',
                  fontSize: '12px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <span>Manage</span>
                <FaArrowRight size={10} />
              </Link>

              {card.addLink && (
                <Link
                  to={card.addLink}
                  style={{
                    color: '#8a8377',
                    fontSize: '11px',
                    fontWeight: 500,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <FaPlusCircle size={10} color="#c4a882" />
                  <span>Add New</span>
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Launch Panel */}
      <div
        style={{
          backgroundColor: '#100f0c',
          border: '1px solid rgba(196, 168, 130, 0.25)',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0 font-weight-bold" style={{ color: '#c4a882', letterSpacing: '0.5px' }}>
            Quick Management Shortcuts
          </h5>
          <span style={{ fontSize: '12px', color: '#8a8377' }}>Direct Navigation</span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: '12px',
          }}
        >
          <Link
            to="/products"
            className="btn text-left p-3"
            style={{
              backgroundColor: '#181714',
              border: '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '10px',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
            }}
          >
            <FaBoxes color="#c4a882" size={16} />
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Products</span>
          </Link>

          <Link
            to="/dealer-store"
            className="btn text-left p-3"
            style={{
              backgroundColor: '#181714',
              border: '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '10px',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
            }}
          >
            <FaStore color="#c4a882" size={16} />
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Dealer Stores</span>
          </Link>

          <Link
            to="/add-blog"
            className="btn text-left p-3"
            style={{
              backgroundColor: '#181714',
              border: '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '10px',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
            }}
          >
            <FaNewspaper color="#c4a882" size={16} />
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Add Blog</span>
          </Link>

          <Link
            to="/add-event"
            className="btn text-left p-3"
            style={{
              backgroundColor: '#181714',
              border: '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '10px',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
            }}
          >
            <FaCalendarAlt color="#c4a882" size={16} />
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Add Event</span>
          </Link>

          <Link
            to="/page-list"
            className="btn text-left p-3"
            style={{
              backgroundColor: '#181714',
              border: '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '10px',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
            }}
          >
            <FaFileAlt color="#c4a882" size={16} />
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Page Manager</span>
          </Link>

          <Link
            to="/testimonial-list"
            className="btn text-left p-3"
            style={{
              backgroundColor: '#181714',
              border: '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '10px',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
            }}
          >
            <FaCommentAlt color="#c4a882" size={16} />
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Testimonials</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Home;
